/**
 * P41 · point 3 — PUBLIC double opt-in page: the one the guardian opens from
 * the link the academy sends them.
 *
 * It's public for the same reason certificate verification is: **the
 * recipient has no session when they open the link**. The credential is the
 * link's 32-byte token, single-use, with an expiry, stored hashed on the
 * server.
 *
 * ── The four failure causes look THE SAME, and it's on purpose ───────────────
 *
 * "Doesn't exist", "already used", "declined" and "expired" all answer 404
 * on the server so as not to be an oracle over other people's tokens. This
 * screen respects that decision: a single message for all four. Telling them
 * apart here would undo the server's work.
 *
 * What IS distinguished is **400**, which only has one cause reachable from
 * here: the guardian doesn't yet have an account at that academy (today the
 * server returns an explicit error instead of creating an account just
 * because someone clicked a link). It's distinguished by the status CODE,
 * not by the message text: the text is server prose and changing it
 * shouldn't break a screen.
 *
 * ── What text is mine and what isn't ─────────────────────────────────────────
 *
 * The names and functional descriptions of the five scopes are UI and live
 * in `./labels.ts`. **The document the guardian accepts doesn't exist yet**:
 * what's promised, who it can be claimed against, and how to withdraw it is
 * the owner's copy to write. Until it exists, this screen says so with the
 * `PENDIENTE-TEXTO-CONSENTIMIENTO-DUENO` marker instead of making up legal
 * text —and that's why the server stores `consent_policy_version = NULL`, so
 * that the day it exists, rows from before can be told apart from rows from
 * after.
 *
 * The buttons are NOT disabled because of that absence: the feature is born
 * off, so reaching this point already requires the academy to have turned it
 * on knowingly, and blocking the flow's only exit would leave the family
 * unable to even decline.
 */
import { msg } from "@lingui/core/macro";
import { type Component, createSignal, For, Match, Show, Switch } from "solid-js";
import { ShieldCheck, ShieldX } from "@fleet/icons";
import {
  academyPeopleQueryKeys,
  type GuardianConsentRequest,
  handleApiResponse,
} from "@space/shared";
import { api, createSafeQuery } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { AppBackground } from "@/components/app-background";
import { Button } from "@/components/ui/button";
import { GlassSurface, Skeleton } from "@fleet/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CONSENT_SCOPE_DESCRIPTIONS, CONSENT_SCOPE_LABELS, labelFor } from "./labels";

/** What the screen has already resolved. `idle` = can still decide. */
type Outcome = "idle" | "confirmed" | "declined";

/** What went wrong when answering. Derived from the HTTP STATUS, never the message. */
type ActionError = "no-account" | "link-dead" | "unknown";

const HTTP_BAD_REQUEST = 400;
const HTTP_NOT_FOUND = 404;

function classifyError(err: unknown): ActionError {
  // `handleApiResponse` hangs `status` off the Error. It's read with `in`
  // and not with a cast because the error comes from the boundary: nothing
  // guarantees its shape.
  const status =
    typeof err === "object" && err !== null && "status" in err ? err.status : undefined;
  if (status === HTTP_BAD_REQUEST) {
    return "no-account";
  }
  if (status === HTTP_NOT_FOUND) {
    return "link-dead";
  }
  return "unknown";
}

export const GuardianConsentPublicView: Component<{ token: string }> = (props) => {
  const { t } = useI18n();
  const [outcome, setOutcome] = createSignal<Outcome>("idle");
  const [pendingAction, setPendingAction] = createSignal<"confirm" | "decline" | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [actionError, setActionError] = createSignal<ActionError | null>(null);

  const request = createSafeQuery<GuardianConsentRequest>(() => ({
    queryKey: academyPeopleQueryKeys.guardianConsentRequest(props.token),
    queryFn: async () =>
      handleApiResponse(
        api.GET("/api/public/guardian-consent", {
          params: { query: { token: props.token } },
        }),
      ),
    // A single-use token is not retried: the second attempt would no longer
    // find it alive and the user would see the error twice, later.
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  }));

  const answer = async (action: "confirm" | "decline") => {
    if (busy()) {
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await handleApiResponse(
        action === "confirm"
          ? api.POST("/api/public/guardian-consent/confirm", { body: { token: props.token } })
          : api.POST("/api/public/guardian-consent/decline", { body: { token: props.token } }),
      );
      setOutcome(action === "confirm" ? "confirmed" : "declined");
    } catch (err) {
      setActionError(classifyError(err));
    } finally {
      setBusy(false);
      setPendingAction(null);
    }
  };

  const scopes = () => request.data?.scopes ?? [];

  return (
    <div class="relative min-h-dvh">
      <AppBackground />
      <div class="mx-auto max-w-2xl space-y-6 p-4 py-10 md:p-8">
        <Switch>
          <Match when={request.isLoading}>
            <Skeleton class="h-96 w-full rounded-3xl" />
          </Match>

          <Match when={request.isError}>
            <GlassSurface class="space-y-3 p-8 text-center" data-testid="guardian-consent-dead">
              <ShieldX class="mx-auto h-10 w-10 text-muted-foreground" />
              <h1 class="text-xl font-bold">{t(msg`This link is no longer valid`)}</h1>
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`It may have expired, it may already have been used, or it may never have existed. Ask the academy to send you a new one.`,
                )}
              </p>
            </GlassSurface>
          </Match>

          <Match when={outcome() === "confirmed"}>
            <GlassSurface class="space-y-3 p-8 text-center" data-testid="guardian-consent-done">
              <ShieldCheck class="mx-auto h-10 w-10 text-primary" />
              <h1 class="text-xl font-bold">{t(msg`Authorization recorded`)}</h1>
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`You can withdraw it whenever you want, from your account or by asking the academy.`,
                )}
              </p>
            </GlassSurface>
          </Match>

          <Match when={outcome() === "declined"}>
            <GlassSurface class="space-y-3 p-8 text-center" data-testid="guardian-consent-declined">
              <ShieldX class="mx-auto h-10 w-10 text-muted-foreground" />
              <h1 class="text-xl font-bold">{t(msg`Request declined`)}</h1>
              <p class="text-sm text-muted-foreground">
                {t(msg`Nothing was authorized. The academy can send you a new request.`)}
              </p>
            </GlassSurface>
          </Match>

          <Match when={request.data}>
            {(req) => (
              <GlassSurface class="space-y-6 p-6 md:p-8" data-testid="guardian-consent-form">
                <header class="space-y-2">
                  <h1 class="text-2xl font-bold tracking-tight">
                    {t(msg`Guardian authorization`)}
                  </h1>
                  <p class="text-sm text-muted-foreground">
                    {(() => {
                      const academy = req().organizationName;
                      const student = req().studentName;
                      return t(
                        msg`${academy} is asking for your authorization as the guardian of ${student}.`,
                      );
                    })()}
                  </p>
                  <Show when={req().relationship}>
                    {(rel) => (
                      <p class="text-xs text-muted-foreground">
                        {(() => {
                          const relationship = rel();
                          return t(msg`Stated relationship: ${relationship}`);
                        })()}
                      </p>
                    )}
                  </Show>
                </header>

                <section class="space-y-3">
                  <h2 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(msg`What you are authorizing`)}
                  </h2>
                  <ul class="space-y-2" data-testid="guardian-consent-scopes">
                    <For each={scopes()}>
                      {(scope) => (
                        <Show when={labelFor(CONSENT_SCOPE_LABELS, scope)}>
                          {(label) => (
                            <li class="rounded-xl border border-border p-3">
                              <p class="text-sm font-semibold">{t(label())}</p>
                              <Show when={labelFor(CONSENT_SCOPE_DESCRIPTIONS, scope)}>
                                {(desc) => (
                                  <p class="mt-1 text-xs text-muted-foreground">{t(desc())}</p>
                                )}
                              </Show>
                            </li>
                          )}
                        </Show>
                      )}
                    </For>
                  </ul>
                </section>

                {/*
                  PENDIENTE-TEXTO-CONSENTIMIENTO-DUENO — this is where the document
                  the guardian accepts goes. It's not made up: the owner writes it.
                  Until it exists, the server stores `consent_policy_version` as
                  NULL and this screen says so instead of faking a text.
                */}
                <section
                  class="rounded-xl border border-dashed border-warning/40 bg-warning/5 p-3"
                  data-testid="guardian-consent-pending-text"
                >
                  <p class="text-xs text-muted-foreground">
                    {t(
                      msg`The full text of this authorization is not published yet. Until it is, what you are agreeing to is exactly the list above, and the academy can explain the rest to you.`,
                    )}
                  </p>
                </section>

                <Show when={actionError()}>
                  {(kind) => (
                    <p class="text-sm text-destructive" data-testid="guardian-consent-error">
                      <Switch fallback={t(msg`We could not record your answer. Please try again.`)}>
                        <Match when={kind() === "no-account"}>
                          {t(
                            msg`The academy has not registered you as a guardian yet. Ask them to add your account and open this link again.`,
                          )}
                        </Match>
                        <Match when={kind() === "link-dead"}>
                          {t(
                            msg`It may have expired, it may already have been used, or it may never have existed. Ask the academy to send you a new one.`,
                          )}
                        </Match>
                      </Switch>
                    </p>
                  )}
                </Show>

                <div class="flex flex-col gap-3 sm:flex-row">
                  <Button
                    class="flex-1"
                    data-testid="guardian-consent-authorize"
                    disabled={busy()}
                    onClick={() => {
                      setPendingAction("confirm");
                    }}
                  >
                    {t(msg`Authorize`)}
                  </Button>
                  <Button
                    variant="outline"
                    class="flex-1"
                    data-testid="guardian-consent-decline"
                    disabled={busy()}
                    onClick={() => {
                      setPendingAction("decline");
                    }}
                  >
                    {t(msg`Decline`)}
                  </Button>
                </div>

                <ConfirmDialog
                  open={pendingAction() === "confirm"}
                  onOpenChange={(open) => {
                    if (!open) {
                      setPendingAction(null);
                    }
                  }}
                  title={t(msg`Authorize this participation?`)}
                  description={t(
                    msg`The academy will record that you authorized it, when, and through this link. You can withdraw it later.`,
                  )}
                  confirmLabel={t(msg`Authorize`)}
                  isLoading={busy()}
                  onConfirm={() => void answer("confirm")}
                />
                <ConfirmDialog
                  open={pendingAction() === "decline"}
                  onOpenChange={(open) => {
                    if (!open) {
                      setPendingAction(null);
                    }
                  }}
                  title={t(msg`Decline this request?`)}
                  description={t(
                    msg`Nothing will be authorized and this link stops working. The academy can send you a new request.`,
                  )}
                  confirmLabel={t(msg`Decline`)}
                  variant="destructive"
                  isLoading={busy()}
                  onConfirm={() => void answer("decline")}
                />
              </GlassSurface>
            )}
          </Match>
        </Switch>
      </div>
    </div>
  );
};
